import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { getAllSales, deleteSale, updateSaleStatus, getSalesByStatus, updatePaymentStatus, updateSaleWithFiles } from '../../supabase/database';
import { FaClipboardList, FaTrash, FaEye, FaTimes, FaCheck, FaCheckSquare, FaFileImage, FaExternalLinkAlt, FaEdit, FaUpload } from 'react-icons/fa';
import Modal from '../../components/ui/Modal';
import { formatOrderNumberDisplay } from '../../utils/orderUtils';
import { migrateSalesWithOrderNumbers, checkSalesWithoutOrderNumbers } from '../../utils/migration';

// Memoized StatusBadge component untuk mencegah re-render yang tidak perlu
const StatusBadge = memo(({ status }) => {
  const statusConfig = useMemo(() => ({
    'menunggu validasi': { bg: 'bg-yellow-100 text-yellow-800', text: 'Menunggu Proses' },
    'ditolak': { bg: 'bg-red-100 text-red-800', text: 'Ditolak' },
    'diproses': { bg: 'bg-blue-100 text-blue-800', text: 'Diproses' },
    'dikirim sebagian': { bg: 'bg-purple-100 text-purple-800', text: 'Dikirim Sebagian' },
    'dikirim seluruhnya': { bg: 'bg-emerald-100 text-emerald-800', text: 'Dikirim Seluruhnya' },
  }), []);

  const config = statusConfig[status] || { bg: 'bg-gray-100 text-gray-800', text: status };

  return (
    <span className={`compact-badge inline-flex leading-4 font-semibold rounded-full ${config.bg}`}>
      {config.text}
    </span>
  );
});

// Memoized PaymentBadge component
const PaymentBadge = memo(({ payment }) => {
  const paymentConfig = useMemo(() => ({
    'belum dibayar': { bg: 'bg-gray-100 text-gray-800', text: 'Belum Dibayar' },
    'uang muka': { bg: 'bg-orange-100 text-orange-800', text: 'Uang Muka' },
    'lunas sebagian': { bg: 'bg-blue-100 text-blue-800', text: 'Lunas Sebagian' },
    'lunas': { bg: 'bg-green-100 text-green-800', text: 'Lunas' },
  }), []);

  const config = paymentConfig[payment] || { bg: 'bg-gray-100 text-gray-800', text: payment };

  return (
    <span className={`compact-badge inline-flex leading-4 font-semibold rounded-full ${config.bg}`}>
      {config.text}
    </span>
  );
});

// Memoized OrderCard component untuk performance
const OrderCard = memo(({ order, onAction, onViewDetails }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Memoized handlers untuk mencegah re-creation
  const toggleOpen = useCallback(() => setIsOpen(prev => !prev), []);
  const handleViewDetails = useCallback(() => onViewDetails(order), [onViewDetails, order]);
  const handleReject = useCallback(() => onAction(order.id, 'ditolak'), [onAction, order.id]);
  const handleProcess = useCallback(() => onAction(order.id, 'diproses'), [onAction, order.id]);

  // Memoized calculations
  const subtotal = useMemo(() => Number(order.subtotal).toLocaleString('id-ID'), [order.subtotal]);
  const customerName = useMemo(() => 
    order.pelanggan?.nama_pelanggan || order.namaSekolah || '-', 
    [order.pelanggan?.nama_pelanggan, order.namaSekolah]
  );

  return (
    <div className="bg-white rounded-lg shadow-sm transition hover:shadow-md border border-gray-200">
      <div className="p-4 flex justify-between items-center cursor-pointer" onClick={toggleOpen}>
        <div>
          <p className="font-bold text-gray-800">{order.nama_mitra || order.mitraEmail}</p>
          <p className="text-sm text-gray-500">Pelanggan: {customerName}</p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-lg font-bold text-green-600">
            Rp {subtotal}
          </span>
          <button className="text-gray-500">
            {isOpen ? <FaCheckSquare /> : <FaClipboardList />}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="border-t border-gray-200 p-4">
          {order.notes && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-1">Catatan dari Mitra:</h4>
              <p className="text-sm text-blue-700">{order.notes}</p>
            </div>
          )}
          <h4 className="font-semibold mb-2">Detail Item:</h4>
          <ul className="space-y-1 text-sm text-gray-600 mb-4">
            {order.items?.map((item, index) => (
              <li key={item.kode_buku || index} className="flex justify-between">
                <span>{item.judul_buku} (x{item.kuantitas})</span>
                <span>Rp {(item.harga_buku * item.kuantitas).toLocaleString('id-ID')}</span>
              </li>
            ))}
          </ul>
          <div className="flex justify-end gap-3">
            <button 
              onClick={handleViewDetails}
              className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-lg transition-colors"
            >
              <FaEye /> Detail
            </button>
            <button 
              onClick={handleReject}
              className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded-lg transition-colors"
            >
              <FaTimes /> Tolak
            </button>
            <button 
              onClick={handleProcess}
              className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg transition-colors"
            >
              <FaCheck /> Proses
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

const SemuaPesanan = () => {
  const [sales, setSales] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [updatingPayment, setUpdatingPayment] = useState({});
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'pending'
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [filteredSales, setFilteredSales] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // New states for individual apply buttons
  const [pendingStatusUpdates, setPendingStatusUpdates] = useState({});
  const [pendingPaymentUpdates, setPendingPaymentUpdates] = useState({});
  
  // Migration states
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [migrationStats, setMigrationStats] = useState(null);
  
  // Image modal states
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');

  // Resi management state
  const [showResiModal, setShowResiModal] = useState(false);
  const [editingResiSale, setEditingResiSale] = useState(null);
  const [resiFormData, setResiFormData] = useState({
    resi_number: '',
    bukti_pembayaran_url: ''
  });
  const [updatingResi, setUpdatingResi] = useState(false);

  // Memoized fetch function untuk menghindari re-creation
  const fetchSales = useCallback(async () => {
    try {
        console.log('🚀 SemuaPesanan: Starting to fetch sales data...');
        setLoading(true);
        const [allSales, pendingSales] = await Promise.all([
          getAllSales(),
          getSalesByStatus('menunggu validasi')
        ]);
        console.log('📊 SemuaPesanan: Fetched data:', {
          allSales: allSales?.length || 0,
          pendingSales: pendingSales?.length || 0
        });
        setSales(allSales || []);
        setPendingOrders(pendingSales || []);
      } catch (error) {
        console.error("❌ SemuaPesanan: Gagal mengambil data penjualan:", error);
        setSales([]);
        setPendingOrders([]);
      } finally {
        setLoading(false);
      }
  }, []);

  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  // Memoized filtered sales untuk performance
  const memoizedFilteredSales = useMemo(() => {
    if (!sales || sales.length === 0) return [];
    
    return sales.filter(sale => {
      const statusMatch = statusFilter === 'all' || sale.status === statusFilter;
      const paymentMatch = paymentFilter === 'all' || sale.pembayaran === paymentFilter;
      return statusMatch && paymentMatch;
    });
  }, [sales, statusFilter, paymentFilter]);

  // Update filteredSales saat dependencies berubah
  useEffect(() => {
    console.log('📝 SemuaPesanan: Updating filtered sales:', {
      originalCount: sales.length,
      filteredCount: memoizedFilteredSales.length,
      statusFilter,
      paymentFilter
    });
    setFilteredSales(memoizedFilteredSales);
  }, [memoizedFilteredSales, sales.length, statusFilter, paymentFilter]);

  // Migration functions
  const checkMigrationStats = useCallback(async () => {
    try {
      const stats = await checkSalesWithoutOrderNumbers();
      setMigrationStats(stats);
    } catch (error) {
      console.error('Error checking migration stats:', error);
    }
  }, []);

  const handleMigration = useCallback(async () => {
    if (!window.confirm('Apakah Anda yakin ingin menambahkan nomor pesanan ke semua data lama? Proses ini tidak dapat dibatalkan.')) {
      return;
    }

    setMigrationLoading(true);
    try {
      const result = await migrateSalesWithOrderNumbers();
      alert(`Migration berhasil! ${result.updated} pesanan diupdate, ${result.errors} error.`);
      
      // Refresh data dan stats
      await Promise.all([fetchSales(), checkMigrationStats()]);
    } catch (error) {
      console.error('Migration error:', error);
      alert('Migration gagal: ' + error.message);
    } finally {
      setMigrationLoading(false);
    }
  }, [fetchSales, checkMigrationStats]);

  // Check migration stats on component mount
  useEffect(() => {
    checkMigrationStats();
  }, [checkMigrationStats]);

  // Memoized action handlers untuk performance
  const handleDeleteClick = useCallback((sale) => {
    setSelectedSale(sale);
    setIsModalOpen(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!selectedSale) return;
    setIsDeleting(true);
    try {
      await deleteSale(selectedSale.id);
      fetchSales();
      alert('Pesanan berhasil dihapus!');
    } catch (err) {
      alert("Gagal menghapus pesanan: " + (err.message || 'Terjadi kesalahan'));
      console.error(err);
    } finally {
      setIsDeleting(false);
      setIsModalOpen(false);
      setSelectedSale(null);
    }
  }, [selectedSale, fetchSales]);

  const handleStatusUpdate = useCallback(async (saleId, newStatus) => {
    setUpdatingStatus(prev => ({ ...prev, [saleId]: true }));
    try {
      await updateSaleStatus(saleId, newStatus);
      fetchSales(); // Refresh the data
      // Clear pending status update
      setPendingStatusUpdates(prev => ({ ...prev, [saleId]: null }));
    } catch (err) {
      alert("Gagal mengupdate status pesanan.");
      console.error(err);
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [saleId]: false }));
    }
  }, [fetchSales]);

  const handlePaymentUpdate = useCallback(async (saleId, newPaymentStatus) => {
    setUpdatingPayment(prev => ({ ...prev, [saleId]: true }));
    try {
      await updatePaymentStatus(saleId, newPaymentStatus);
      fetchSales(); // Refresh the data
      // Clear pending payment update
      setPendingPaymentUpdates(prev => ({ ...prev, [saleId]: null }));
    } catch (err) {
      alert("Gagal mengupdate status pembayaran.");
      console.error(err);
    } finally {
      setUpdatingPayment(prev => ({ ...prev, [saleId]: false }));
    }
  }, [fetchSales]);

  // New handlers for individual apply buttons
  const handleStatusChange = useCallback((saleId, newStatus) => {
    setPendingStatusUpdates(prev => ({ ...prev, [saleId]: newStatus }));
  }, []);

  const handlePaymentChange = useCallback((saleId, newPaymentStatus) => {
    setPendingPaymentUpdates(prev => ({ ...prev, [saleId]: newPaymentStatus }));
  }, []);

  const applyStatusUpdate = useCallback((saleId) => {
    const newStatus = pendingStatusUpdates[saleId];
    if (newStatus) {
      handleStatusUpdate(saleId, newStatus);
    }
  }, [pendingStatusUpdates, handleStatusUpdate]);

  const applyPaymentUpdate = useCallback((saleId) => {
    const newPaymentStatus = pendingPaymentUpdates[saleId];
    if (newPaymentStatus) {
      handlePaymentUpdate(saleId, newPaymentStatus);
    }
  }, [pendingPaymentUpdates, handlePaymentUpdate]);

  const handleValidationAction = useCallback(async (orderId, newStatus) => {
    try {
      console.log('🔄 handleValidationAction: Attempting to update sale status', {
        orderId,
        newStatus,
        action: newStatus === 'diproses' ? 'memproses' : 'menolak'
      });
      
      await updateSaleStatus(orderId, newStatus);
      
      console.log('✅ handleValidationAction: Successfully updated sale status');
      
      setPendingOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
      // Also update the main sales list
      fetchSales();
      
      // Show success message
      alert(`Pesanan berhasil ${newStatus === 'diproses' ? 'diproses' : 'ditolak'}!`);
    } catch (err) {
      console.error('❌ handleValidationAction: Error updating sale status:', {
        orderId,
        newStatus,
        error: err,
        errorMessage: err.message,
        errorCode: err.code
      });
      
      // Provide more detailed error message
      let errorMessage = `Gagal ${newStatus === 'diproses' ? 'memproses' : 'menolak'} pesanan.`;
      
      if (err.message) {
        errorMessage += ` Detail: ${err.message}`;
      }
      
      alert(errorMessage);
    }
  }, [fetchSales]);

  const handleViewDetails = useCallback((sale) => {
    setSelectedSale(sale);
    setShowDetailModal(true);
  }, []);

  const closeDetailModal = useCallback(() => {
    setShowDetailModal(false);
    setSelectedSale(null);
  }, []);
  
  const handleImageClick = useCallback((imageUrl) => {
    setSelectedImageUrl(imageUrl);
    setShowImageModal(true);
  }, []);
  
  const closeImageModal = useCallback(() => {
    setShowImageModal(false);
    setSelectedImageUrl('');
  }, []);

  // Resi management functions
  const handleResiEdit = useCallback((sale) => {
    setEditingResiSale(sale);
    setResiFormData({
      resi_number: sale.resi_number || ''
    });
    setShowResiModal(true);
  }, []);

  const handleResiUpdate = useCallback(async () => {
    if (!editingResiSale) return;
    
    setUpdatingResi(true);
    try {
      // Hanya update nomor resi, tidak mengubah bukti pembayaran
      await updateSaleWithFiles(editingResiSale.id, {
        resi_number: resiFormData.resi_number.trim() || null
      });
      
      // Refresh data
      await fetchSales();
      
      alert('Nomor resi berhasil diperbarui!');
      setShowResiModal(false);
      setEditingResiSale(null);
      setResiFormData({ resi_number: '' });
    } catch (error) {
      console.error('Error updating resi:', error);
      alert('Gagal memperbarui nomor resi: ' + error.message);
    } finally {
      setUpdatingResi(false);
    }
  }, [editingResiSale, resiFormData, fetchSales]);

  const closeResiModal = useCallback(() => {
    setShowResiModal(false);
    setEditingResiSale(null);
    setResiFormData({ resi_number: '' });
    setUpdatingResi(false);
  }, []);

  // Memoized bulk select functions
  const handleSelectAll = useCallback((checked) => {
    if (checked) {
      setSelectedItems(filteredSales.map(sale => sale.id));
    } else {
      setSelectedItems([]);
    }
  }, [filteredSales]);

  const handleSelectItem = useCallback((itemId, checked) => {
    if (checked) {
      setSelectedItems(prev => [...prev, itemId]);
    } else {
      setSelectedItems(prev => prev.filter(id => id !== itemId));
    }
  }, []);

  const handleBulkAction = useCallback(async () => {
    if (!bulkAction || selectedItems.length === 0) {
      alert('Pilih tindakan dan minimal satu item.');
      return;
    }

    setShowBulkModal(true);
  }, [bulkAction, selectedItems.length]);

  const confirmBulkAction = useCallback(async () => {
    try {
      if (bulkAction === 'delete') {
        // Delete selected items
        for (const itemId of selectedItems) {
          await deleteSale(itemId);
        }
        alert(`${selectedItems.length} pesanan berhasil dihapus.`);
      } else {
        // Update status or payment for selected items
        const isPaymentAction = ['belum dibayar', 'uang muka', 'lunas sebagian', 'lunas'].includes(bulkAction);
        
        for (const itemId of selectedItems) {
          if (isPaymentAction) {
            await updatePaymentStatus(itemId, bulkAction);
          } else {
            await updateSaleStatus(itemId, bulkAction);
          }
        }
        
        const actionText = isPaymentAction ? 'status pembayaran' : 'status pesanan';
        alert(`${selectedItems.length} ${actionText} berhasil diperbarui.`);
      }
      
      // Reset selection and refresh data
      setSelectedItems([]);
      setBulkAction('');
      fetchSales();
    } catch (error) {
      alert('Terjadi kesalahan saat melakukan tindakan bulk.');
      console.error(error);
    } finally {
      setShowBulkModal(false);
    }
  }, [bulkAction, selectedItems, fetchSales]);

  // Effect to ensure scrollbar is always visible and synced
  useEffect(() => {
    const tableContainer = document.querySelector('.table-container');
    const customScrollbar = document.querySelector('.custom-scrollbar');
    
    if (tableContainer && customScrollbar) {
      // Sync custom scrollbar with table scroll
      const syncScrollbars = () => {
        customScrollbar.scrollLeft = tableContainer.scrollLeft;
      };
      
      const syncTableScroll = () => {
        tableContainer.scrollLeft = customScrollbar.scrollLeft;
      };
      
      // Add event listeners
      tableContainer.addEventListener('scroll', syncScrollbars);
      customScrollbar.addEventListener('scroll', syncTableScroll);
      
      // Initial sync
      syncScrollbars();
      
      return () => {
        tableContainer.removeEventListener('scroll', syncScrollbars);
        customScrollbar.removeEventListener('scroll', syncTableScroll);
      };
    }
  }, [filteredSales]);

  return (
    <div className="p-4 md:p-8 bg-white min-h-full">
      {/* Custom scrollbar styles */}
      <style jsx global>{`
        .table-wrapper {
          position: relative;
          width: 100%;
          overflow: visible;
        }
        
        .table-container {
          overflow-x: auto;
          overflow-y: visible;
          width: 100%;
          position: relative;
          border-radius: 8px;
        }
        
        /* Custom horizontal scrollbar that's always visible */
        .custom-scrollbar {
          position: sticky;
          bottom: 0;
          left: 0;
          right: 0;
          height: 20px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          margin-top: 8px;
          overflow-x: auto;
          overflow-y: hidden;
          z-index: 10;
        }
        
        .custom-scrollbar::-webkit-scrollbar {
          height: 16px;
          background: #f1f5f9;
          border-radius: 8px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 8px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #94a3b8;
          border-radius: 8px;
          border: 2px solid #f1f5f9;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #64748b;
        }
        
        .scrollbar-content {
          width: 1600px;
          height: 1px;
          background: transparent;
        }
        
        /* Hide original scrollbar */
        .table-container::-webkit-scrollbar {
          display: none;
        }
        
        .table-container {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        /* Table specific styling */
        .admin-table {
          width: 1600px !important;
          min-width: 1600px !important;
          table-layout: fixed;
        }
        
        /* Compact table styles */
        .admin-table-compact {
          font-size: 13px;
        }
        .admin-table-compact th {
          padding: 8px 12px !important;
          vertical-align: top;
          line-height: 1.2;
        }
        .admin-table-compact td {
          padding: 8px 12px !important;
          vertical-align: top;
          line-height: 1.3;
        }
        .compact-status-cell {
          min-height: 80px;
        }
        .compact-badge {
          font-size: 11px;
          padding: 2px 8px;
          white-space: nowrap;
        }
        .compact-dropdown {
          font-size: 11px;
          padding: 2px 4px;
        }
        .compact-button {
          padding: 2px 6px;
          font-size: 11px;
          min-width: 30px;
          height: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>
      
      <Modal
        isOpen={isModalOpen}
        onClose={() => !isDeleting && setIsModalOpen(false)}
        onConfirm={confirmDelete}
        title="Konfirmasi Hapus Pesanan"
        type="confirm"
        loading={isDeleting}
      >
        {selectedSale && (
          <div className="space-y-3">
            <p className="text-gray-700">
              Anda akan menghapus pesanan berikut:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg border">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">No. Pesanan:</span>
                  <span className="font-mono text-gray-900">{formatOrderNumberDisplay(selectedSale.noPesanan)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">Mitra:</span>
                  <span className="text-gray-900">{selectedSale.nama_mitra || selectedSale.mitraEmail}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">Pelanggan:</span>
                  <span className="text-gray-900">{selectedSale.pelanggan?.nama_pelanggan || selectedSale.namaSekolah || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">Total:</span>
                  <span className="font-semibold text-green-600">Rp {selectedSale.subtotal?.toLocaleString('id-ID')}</span>
                </div>
              </div>
            </div>
            <p className="text-sm text-red-600 font-medium">
              ⚠️ Tindakan ini tidak dapat diurungkan dan akan menghapus semua data terkait pesanan ini.
            </p>
          </div>
        )}
      </Modal>
      
      <div className="flex items-center gap-4 mb-6">
        <FaClipboardList className="text-4xl text-amber-500" />
        <h1 className="text-3xl font-bold text-gray-800">Kelola Pesanan Mitra</h1>
      </div>

      {/* Migration Status & Button */}
      {migrationStats && migrationStats.withoutOrderNumber > 0 && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-yellow-800">Update Nomor Pesanan</h3>
              <p className="text-yellow-700">
                Ditemukan {migrationStats.withoutOrderNumber} pesanan tanpa nomor pesanan. 
                Klik tombol di samping untuk menambahkan nomor pesanan otomatis.
              </p>
              <p className="text-sm text-yellow-600 mt-1">
                Total pesanan: {migrationStats.total} | Sudah ada nomor: {migrationStats.withOrderNumber}
              </p>
            </div>
            <button
              onClick={handleMigration}
              disabled={migrationLoading}
              className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-400 text-white font-bold py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
            >
              {migrationLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Memproses...
                </>
              ) : (
                'Update Nomor Pesanan'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('all')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'all'
                  ? 'border-amber-500 text-amber-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Semua Pesanan ({filteredSales.length})
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'pending'
                  ? 'border-amber-500 text-amber-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Menunggu Validasi ({pendingOrders.length})
            </button>
          </nav>
        </div>
      </div>

      {/* Filters - only show for "All Orders" tab */}
      {activeTab === 'all' && (
        <div className="mb-6 bg-gray-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Filter Pesanan</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <option value="all">Semua Status</option>
                <option value="menunggu validasi">Menunggu Proses</option>
                <option value="diproses">Diproses</option>
                <option value="dikirim sebagian">Dikirim Sebagian</option>
                <option value="dikirim seluruhnya">Dikirim Seluruhnya</option>
                <option value="ditolak">Ditolak</option>
              </select>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Pembayaran</label>
              <select
                value={paymentFilter}
                onChange={(e) => setPaymentFilter(e.target.value)}
                className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <option value="all">Semua Pembayaran</option>
                <option value="belum dibayar">Belum Dibayar</option>
                <option value="uang muka">Uang Muka</option>
                <option value="lunas sebagian">Lunas Sebagian</option>
                <option value="lunas">Lunas</option>
              </select>
            </div>

            <div className="md:col-span-2 lg:col-span-2 flex items-end">
              <button
                onClick={() => {
                  setStatusFilter('all');
                  setPaymentFilter('all');
                }}
                className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors duration-200"
              >
                Reset Filter
              </button>
              <div className="ml-4 text-sm text-gray-600 flex items-center">
                Menampilkan {filteredSales.length} dari {sales.length} pesanan
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Actions - only show for "All Orders" tab */}
      {activeTab === 'all' && selectedItems.length > 0 && (
        <div className="mb-6 bg-blue-50 border border-blue-200 p-4 rounded-lg">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="text-sm text-blue-800">
              <strong>{selectedItems.length}</strong> item terpilih
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <select
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
                className="text-sm border border-blue-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">Pilih Tindakan</option>
                <optgroup label="Status Pesanan">
                  <option value="menunggu validasi">Ubah ke Menunggu Proses</option>
                  <option value="diproses">Ubah ke Diproses</option>
                  <option value="dikirim sebagian">Ubah ke Dikirim Sebagian</option>
                  <option value="dikirim seluruhnya">Ubah ke Dikirim Seluruhnya</option>
                  <option value="ditolak">Ubah ke Ditolak</option>
                </optgroup>
                <optgroup label="Status Pembayaran">
                  <option value="belum dibayar">Ubah ke Belum Dibayar</option>
                  <option value="uang muka">Ubah ke Uang Muka</option>
                  <option value="lunas sebagian">Ubah ke Lunas Sebagian</option>
                  <option value="lunas">Ubah ke Lunas</option>
                </optgroup>
                <optgroup label="Tindakan Lain">
                  <option value="delete">Hapus Item</option>
                </optgroup>
              </select>
              
              <button
                onClick={handleBulkAction}
                disabled={!bulkAction}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm rounded-lg transition-colors duration-200"
              >
                Terapkan
              </button>
              
              <button
                onClick={() => setSelectedItems([])}
                className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors duration-200"
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
        </div>
      ) : activeTab === 'pending' ? (
        // Pending Orders Tab (Card View)
        <div>
          {pendingOrders.length === 0 ? (
            <div className="bg-gray-50 p-6 rounded-lg text-center text-gray-500">
              Tidak ada pesanan yang perlu divalidasi saat ini.
            </div>
          ) : (
            <div className="space-y-4">
              {pendingOrders.map(order => (
                <OrderCard 
                  key={order.id} 
                  order={order} 
                  onAction={handleValidationAction}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        // All Orders Tab (Table View)
        <div className="table-wrapper">
          <div 
            className="table-container bg-white rounded-lg shadow"
            style={{
              border: '1px solid #E5E7EB',
              height: 'auto',
              maxHeight: 'none'
            }}
          >
            <table className="admin-table admin-table-compact divide-y divide-gray-200" style={{ width: '1600px', minWidth: '1600px' }}>
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8">
                <input
                  type="checkbox"
                  checked={selectedItems.length === filteredSales.length && filteredSales.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24">Tanggal</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-32">Nama Mitra</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-40">Pelanggan</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24">Subtotal</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-48">Status</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-48">Pembayaran</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-32">Resi</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-28">Bukti Bayar</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-20">Aksi</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-48">Catatan</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-36">No Pesanan</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredSales.map(sale => (
              <tr key={sale.id} className={selectedItems.includes(sale.id) ? 'bg-blue-50' : ''}>
                <td className="px-2 py-3 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(sale.id)}
                    onChange={(e) => handleSelectItem(sale.id, e.target.checked)}
                    className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  />
                </td>
                <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                  <div className="max-w-24 truncate">
                    {sale.createdAt ? new Date(sale.createdAt).toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-'}
                  </div>
                </td>
                <td className="px-3 py-3 text-sm font-medium text-gray-900">
                  <div className="max-w-32">
                    <div className="truncate mb-1" title={sale.nama_mitra || sale.mitraEmail}>
                      {sale.nama_mitra || sale.mitraEmail}
                    </div>
                    <button 
                      onClick={() => handleViewDetails(sale)} 
                      className="px-2 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 text-xs rounded transition-colors duration-200"
                      title="Lihat detail pesanan"
                    >
                      Lihat Detail Pesanan
                    </button>
                  </div>
                </td>
                <td className="px-3 py-3 text-sm text-gray-500">
                  <div className="max-w-40 truncate" title={sale.pelanggan?.nama_pelanggan || sale.namaSekolah || 'Data pelanggan belum lengkap'}>
                    {sale.pelanggan?.nama_pelanggan || sale.namaSekolah || (
                      <span className="text-gray-400 italic text-xs">Data belum lengkap</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                  <div className="max-w-24 truncate">
                    Rp {sale.subtotal?.toLocaleString('id-ID')}
                  </div>
                </td>
                <td className="compact-status-cell">
                  <div className="flex flex-col gap-1.5 w-48">
                    {/* Current Status Badge */}
                    <div className="flex justify-center">
                      <StatusBadge status={sale.status} />
                    </div>
                    
                    {/* Update Controls */}
                    <div className="flex gap-1">
                      <select
                        value={pendingStatusUpdates[sale.id] ?? sale.status}
                        onChange={(e) => handleStatusChange(sale.id, e.target.value)}
                        disabled={updatingStatus[sale.id]}
                        className="compact-dropdown flex-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-amber-500 disabled:bg-gray-100"
                      >
                        <option value="menunggu validasi">Menunggu</option>
                        <option value="diproses">Diproses</option>
                        <option value="dikirim sebagian">Sebagian</option>
                        <option value="dikirim seluruhnya">Seluruh</option>
                        <option value="ditolak">Ditolak</option>
                      </select>
                      <button
                        onClick={() => applyStatusUpdate(sale.id)}
                        disabled={updatingStatus[sale.id] || !pendingStatusUpdates[sale.id] || pendingStatusUpdates[sale.id] === sale.status}
                        className="compact-button bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded transition-colors duration-200"
                      >
                        {updatingStatus[sale.id] ? (
                          <div className="animate-spin rounded-full h-2.5 w-2.5 border-b-2 border-white mx-auto"></div>
                        ) : (
                          '✓'
                        )}
                      </button>
                    </div>
                  </div>
                </td>
                <td className="compact-status-cell">
                  <div className="flex flex-col gap-1.5 w-48">
                    {/* Current Payment Badge */}
                    <div className="flex justify-center">
                      <PaymentBadge payment={sale.pembayaran || 'belum dibayar'} />
                    </div>
                    
                    {/* Update Controls */}
                    <div className="flex gap-1">
                      <select
                        value={pendingPaymentUpdates[sale.id] ?? sale.pembayaran ?? 'belum dibayar'}
                        onChange={(e) => handlePaymentChange(sale.id, e.target.value)}
                        disabled={updatingPayment[sale.id]}
                        className="compact-dropdown flex-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-amber-500 disabled:bg-gray-100"
                      >
                        <option value="belum dibayar">Belum Bayar</option>
                        <option value="uang muka">Uang Muka</option>
                        <option value="lunas sebagian">Sebagian</option>
                        <option value="lunas">Lunas</option>
                      </select>
                      <button
                        onClick={() => applyPaymentUpdate(sale.id)}
                        disabled={updatingPayment[sale.id] || !pendingPaymentUpdates[sale.id] || pendingPaymentUpdates[sale.id] === (sale.pembayaran || 'belum dibayar')}
                        className="compact-button bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded transition-colors duration-200"
                      >
                        {updatingPayment[sale.id] ? (
                          <div className="animate-spin rounded-full h-2.5 w-2.5 border-b-2 border-white mx-auto"></div>
                        ) : (
                          '✓'
                        )}
                      </button>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    {sale.resi_number ? (
                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full max-w-24 truncate" title={sale.resi_number}>
                        {sale.resi_number}
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">-</span>
                    )}
                    <button
                      onClick={() => handleResiEdit(sale)}
                      className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-all duration-200"
                      title="Upload/Edit resi"
                    >
                      <FaUpload className="text-xs" />
                    </button>
                  </div>
                </td>
                <td className="px-3 py-3 whitespace-nowrap text-sm">
                  <div className="max-w-28">
                    {sale.bukti_pembayaran_url ? (
                      <button
                        onClick={() => handleImageClick(sale.bukti_pembayaran_url)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full hover:bg-green-200 transition-colors cursor-pointer"
                        title="Lihat bukti pembayaran"
                      >
                        <FaFileImage />
                        Bukti
                      </button>
                    ) : (
                      <span className="text-gray-400 text-xs">Belum ada</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center justify-center">
                    <button 
                      onClick={() => handleDeleteClick(sale)} 
                      className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-all duration-200"
                      title="Hapus pesanan"
                    >
                      <FaTrash className="text-sm" />
                    </button>
                  </div>
                </td>
                <td className="px-3 py-3 text-sm text-gray-500">
                  <div className="max-w-48 truncate leading-tight" title={sale.notes || '-'}>
                    {sale.notes || '-'}
                  </div>
                </td>
                <td className="px-3 py-3 whitespace-nowrap text-sm font-mono text-gray-900">
                  <div className="max-w-36 truncate" title={formatOrderNumberDisplay(sale.noPesanan)}>
                    {formatOrderNumberDisplay(sale.noPesanan)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          </table>
          {!loading && filteredSales.length === 0 && sales.length === 0 && (
            <div className="text-center p-8 text-gray-500">
              <p>Belum ada pesanan.</p>
            </div>
          )}
          {!loading && filteredSales.length === 0 && sales.length > 0 && (
            <div className="text-center p-8 text-gray-500">
              <p>Tidak ada pesanan yang sesuai dengan filter.</p>
              <button
                onClick={() => {
                  setStatusFilter('all');
                  setPaymentFilter('all');
                }}
                className="mt-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm rounded-lg transition-colors duration-200"
              >
                Reset Filter
              </button>
            </div>
          )}
          </div>
          
          {/* Custom horizontal scrollbar - always visible */}
          <div className="custom-scrollbar">
            <div className="scrollbar-content"></div>
          </div>
        </div>
      )}

      {/* Bulk Action Confirmation Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Konfirmasi Tindakan Bulk</h2>
              <p className="text-gray-600 mb-6">
                Apakah Anda yakin ingin menerapkan tindakan ini pada <strong>{selectedItems.length}</strong> item yang dipilih?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowBulkModal(false)}
                  className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors duration-200"
                >
                  Batal
                </button>
                <button
                  onClick={confirmBulkAction}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200"
                >
                  Ya, Terapkan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-800">Detail Pesanan</h2>
              <button
                onClick={closeDetailModal}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
              >
                <FaTimes className="text-lg" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Order Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-800 mb-3">Informasi Pesanan</h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="font-medium">No Pesanan:</span> <span className="font-mono text-blue-600">{formatOrderNumberDisplay(selectedSale.noPesanan)}</span></div>
                    <div><span className="font-medium">Tanggal:</span> {selectedSale.createdAt ? new Date(selectedSale.createdAt).toLocaleDateString('id-ID') : '-'}</div>
                    <div><span className="font-medium">Status:</span> <StatusBadge status={selectedSale.status} /></div>
                    <div><span className="font-medium">Kode Mitra:</span> {selectedSale.kode_mitra}</div>
                    <div><span className="font-medium">Nama Mitra:</span> {selectedSale.nama_mitra}</div>
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-800 mb-3">Informasi Pelanggan</h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="font-medium">Nama:</span> {selectedSale.pelanggan?.nama_pelanggan || selectedSale.namaSekolah || '-'}</div>
                    <div><span className="font-medium">No. WhatsApp:</span> {selectedSale.pelanggan?.no_wa || '-'}</div>
                    <div><span className="font-medium">Alamat:</span> {selectedSale.pelanggan?.alamat || '-'}</div>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-800 mb-3">Item Pesanan</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produk</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Harga</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Qty</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Diskon</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedSale.items?.map((item, index) => {
                        const hargaSetelahDiskon = item.harga_buku - (item.harga_buku * (item.diskonPerItem || 0) / 100);
                        const totalItem = hargaSetelahDiskon * item.kuantitas;
                        return (
                          <tr key={index}>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.judul_buku}</td>
                            <td className="px-4 py-3 text-sm text-gray-700 text-right">Rp {item.harga_buku.toLocaleString('id-ID')}</td>
                            <td className="px-4 py-3 text-sm text-gray-700 text-center">{item.kuantitas}</td>
                            <td className="px-4 py-3 text-sm text-gray-700 text-center">{item.diskonPerItem || 0}%</td>
                            <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">Rp {totalItem.toLocaleString('id-ID')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Order Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  {selectedSale.notes && (
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-gray-800 mb-2">Catatan</h3>
                      <p className="text-sm text-gray-700">{selectedSale.notes}</p>
                    </div>
                  )}
                  {/* Resi & Bukti Pembayaran */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-2">Pengiriman & Bukti Pembayaran</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-700">Nomor Resi:</span>
                        <span className="font-medium text-gray-900">{selectedSale.resi_number || '-'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-700">Bukti Pembayaran:</span>
                        {selectedSale.bukti_pembayaran_url ? (
                          <button
                            onClick={() => handleImageClick(selectedSale.bukti_pembayaran_url)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full hover:bg-green-200 transition-colors cursor-pointer"
                          >
                            <FaFileImage />
                            Lihat Bukti
                          </button>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-800 mb-3">Ringkasan Pembayaran</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Status Pembayaran:</span>
                      <span><PaymentBadge payment={selectedSale.pembayaran || 'belum dibayar'} /></span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Harga:</span>
                      <span>Rp {(selectedSale.total_harga || selectedSale.totalHarga || 0).toLocaleString('id-ID')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-red-600">Diskon:</span>
                      <span className="text-red-600">- Rp {(((selectedSale.total_harga || selectedSale.totalHarga || 0) - (selectedSale.harga_setelah_diskon || selectedSale.hargaSetelahDiskon || 0))).toLocaleString('id-ID')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Ongkos Kirim:</span>
                      <span>Rp {(selectedSale.ongkir || 0).toLocaleString('id-ID')}</span>
                    </div>
                    <hr className="my-2" />
                    <div className="flex justify-between font-bold text-lg">
                      <span>Total:</span>
                      <span>Rp {(selectedSale.subtotal || 0).toLocaleString('id-ID')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end p-6 border-t border-gray-200">
              <button
                onClick={closeDetailModal}
                className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors duration-200"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Edit Resi */}
      {showResiModal && editingResiSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-800">Input Nomor Resi</h2>
              <button
                onClick={closeResiModal}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
              >
                <FaTimes className="text-lg" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <div className="text-sm">
                  <div className="font-medium text-gray-800">Pesanan: {editingResiSale.noPesanan || '-'}</div>
                  <div className="text-gray-600">Mitra: {editingResiSale.nama_mitra || '-'}</div>
                  <div className="text-gray-600">Pelanggan: {editingResiSale.pelanggan?.nama_pelanggan || editingResiSale.namaSekolah || '-'}</div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nomor Resi Pengiriman
                </label>
                <input
                  type="text"
                  value={resiFormData.resi_number}
                  onChange={(e) => setResiFormData(prev => ({ ...prev, resi_number: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Masukkan nomor resi..."
                />
              </div>

              {/* Preview Bukti Pembayaran dari Mitra */}
              {editingResiSale.bukti_pembayaran_url && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bukti Pembayaran dari Mitra
                  </label>
                  <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <button
                      onClick={() => handleImageClick(editingResiSale.bukti_pembayaran_url)}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition-colors cursor-pointer"
                    >
                      <FaFileImage /> Lihat Bukti Pembayaran
                    </button>
                    <p className="text-xs text-gray-500 mt-2">Bukti pembayaran diupload oleh mitra</p>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={closeResiModal}
                className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors duration-200"
                disabled={updatingResi}
              >
                Batal
              </button>
              <button
                onClick={handleResiUpdate}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 flex items-center gap-2"
                disabled={updatingResi}
              >
                {updatingResi ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Menyimpan...
                  </>
                ) : (
                  'Simpan Perubahan'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeImageModal}
        >
          <div 
            className="relative bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Bukti Pembayaran</h3>
              <button
                onClick={closeImageModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 text-center">
              <img
                src={selectedImageUrl}
                alt="Bukti Pembayaran"
                className="max-w-full max-h-[70vh] object-contain mx-auto"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik05NCA4NEM5NCA3OS41ODE3IDk3LjU4MTcgNzYgMTAyIDc2QzEwNi40MTggNzYgMTEwIDc5LjU4MTcgMTEwIDg0VjEwMEMxMTAgMTA0LjQxOCAxMDYuNDE4IDEwOCAxMDIgMTA4Qzk3LjU4MTcgMTA4IDk0IDEwNC40MTggOTQgMTAwVjg0WiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMTAyIDEyNEM5Ny41ODE3IDEyNCA5NCAxMjAuNDE4IDk0IDExNkM5NCAxMTEuNTgyIDk3LjU4MTcgMTA4IDEwMiAxMDhDMTA2LjQxOCAxMDggMTEwIDExMS41ODIgMTEwIDExNkMxMTAgMTIwLjQxOCAxMDYuNDE4IDEyNCAxMDIgMTI0WiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4='; // Fallback image
                  e.target.alt = 'Gambar tidak dapat dimuat';
                }}
              />
              <p className="text-sm text-gray-500 mt-2">Klik di luar gambar untuk menutup</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SemuaPesanan;
